// src/pages/ClientDashboard.js
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ClientBackground from "../components/ClientBackground";
import api from "../api";
import LoadingSpinner from "../components/LoadingSpinner";

const calculateTimeToBirth = (dob) => {
  const today = new Date();
  const birthDate = new Date(dob);
  const diffTime = birthDate - today;
  if (diffTime < 0) return "Due date passed";

  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30.44);

  if (diffMonths > 0) {
    return `in ${diffMonths} month${diffMonths > 1 ? 's' : ''}`;
  }
  if (diffWeeks > 0) {
    return `in ${diffWeeks} week${diffWeeks > 1 ? 's' : ''}`;
  }
  return `in ${diffDays} day${diffDays > 1 ? 's' : ''}`;
};

export default function ClientDashboard() {
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchChildren = async () => {
      try {
        const accessToken = localStorage.getItem('access');
        const response = await api.get("/children/profiles/", {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        setChildren(response.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchChildren();
  }, []);




  const [activeIndex, setActiveIndex] = useState(0);
  const cardScrollRef = useRef(null);
  const imageScrollRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const isSyncingRef = useRef(false);

  const scrollContainerToChildCenter = useCallback((container, childEl, behavior = 'smooth') => {
    if (!container || !childEl) return;

    const containerCenter = container.offsetWidth / 2;
    const childCenter = childEl.offsetLeft + childEl.offsetWidth / 2;
    const scrollLeft = childCenter - containerCenter;

    container.scrollTo({
      left: scrollLeft,
      behavior: behavior,
    });
  }, []);

  const setEdgePaddingToCenterItems = useCallback((container, childWidthPx) => {
    if (!container) return;

    const containerWidth = container.offsetWidth;
    const padding = (containerWidth - childWidthPx) / 2;
    container.style.paddingLeft = `${padding}px`;
    container.style.paddingRight = `${padding}px`;
  }, []);

  const centerCardsAtIndex = useCallback((index, behavior = 'smooth') => {
    if (!cardScrollRef.current) return;

    const container = cardScrollRef.current;
    const targetCard = container.children[index];

    if (targetCard) {
      scrollContainerToChildCenter(container, targetCard, behavior);
    }
  }, [scrollContainerToChildCenter]);

  const centerAvatarsAtIndex = useCallback((index, behavior = 'smooth') => {
    if (!imageScrollRef.current) return;

    const container = imageScrollRef.current;
    const targetAvatar = container.children[index];

    if (targetAvatar) {
      scrollContainerToChildCenter(container, targetAvatar, behavior);
    }
  }, [scrollContainerToChildCenter]);

  const setupPadding = useCallback(() => {
    if (cardScrollRef.current) {
      const firstCard = cardScrollRef.current.children[0];
      if (firstCard) {
        const cardWidth = firstCard.offsetWidth;
        setEdgePaddingToCenterItems(cardScrollRef.current, cardWidth);
      }
    }

    if (imageScrollRef.current) {
      const activeAvatarWidth = 96;
      setEdgePaddingToCenterItems(imageScrollRef.current, activeAvatarWidth);
    }
  }, [setEdgePaddingToCenterItems]);

  useEffect(() => {
    if (children.length > 0) {
      setupPadding();
      const initialIndex = Math.floor(children.length / 2);
      setActiveIndex(initialIndex);
      const timer = setTimeout(() => {
        centerCardsAtIndex(initialIndex, 'auto');
        centerAvatarsAtIndex(initialIndex, 'auto');
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [children.length, setupPadding, centerCardsAtIndex, centerAvatarsAtIndex]);

  useEffect(() => {
    const handleResize = () => {
      setupPadding();
      centerCardsAtIndex(activeIndex, 'auto');
      centerAvatarsAtIndex(activeIndex, 'auto');
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [activeIndex, setupPadding, centerCardsAtIndex, centerAvatarsAtIndex]);

  const handleCardScroll = useCallback(() => {
    if (isSyncingRef.current) return;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (!cardScrollRef.current) return;

      const container = cardScrollRef.current;
      const containerCenter = container.offsetWidth / 2;

      let closestIndex = 0;
      let minDistance = Infinity;

      Array.from(container.children).forEach((card, index) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2 - container.scrollLeft;
        const distance = Math.abs(cardCenter - containerCenter);

        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== activeIndex) {
        setActiveIndex(closestIndex);
        isSyncingRef.current = true;
        centerAvatarsAtIndex(closestIndex, 'smooth');
        setTimeout(() => {
          isSyncingRef.current = false;
        }, 600);
      }
    }, 80);
  }, [activeIndex, centerAvatarsAtIndex]);

  const handleAvatarClick = useCallback((index) => {
    if (index === activeIndex) return;

    isSyncingRef.current = true;
    setActiveIndex(index);
    centerCardsAtIndex(index, 'smooth');
    centerAvatarsAtIndex(index, 'smooth');

    setTimeout(() => {
      isSyncingRef.current = false;
    }, 600);
  }, [activeIndex, centerCardsAtIndex, centerAvatarsAtIndex]);

  if (loading) {
    return <LoadingSpinner fullScreen={true} text="Loading Children..." />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-red-500">Error fetching data. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="relative h-screen flex flex-col">
      <ClientBackground />

      <div className="relative flex-grow flex flex-col" style={{ zIndex: 10 }}>
        <div
          ref={cardScrollRef}

          onScroll={handleCardScroll}
          className="flex snap-x snap-mandatory gap-8 overflow-x-auto scrollbar-hide pb-8 mt-20"
        >

          {children.map((child, index) => (
            <div
              key={child.id}
              className={`group relative flex-shrink-0 snap-center transition-all duration-300 ${activeIndex === index ? "opacity-100 scale-100" : "opacity-50 scale-90"
                }`}
              style={{ width: '90vw', maxWidth: '400px' }}
            >
              <div onClick={() => navigate(child.pregnancystage ? `/pregnancy/${child.id}` : `/child/${child.id}`)} className="w-full">
                <div className="relative flex flex-col items-center pt-16">
                  <div className="absolute top-0 z-20 h-28 w-28 overflow-hidden rounded-full bg-gradient-to-br from-[#FDB913] to-[#FFA500] p-2 shadow-2xl ring-4 ring-white">
                    <img
                      src={child.profile_image}
                      alt={child.name}
                      className="h-full w-full rounded-full object-cover"
                      onLoad={() => console.log(`Image loaded for ${child.first_name}: ${child.profile_image}`)}
                      onError={(e) => console.error(`Error loading image for ${child.first_name}:`, e.target.src)}
                    />
                  </div>

                  <div className="w-full rounded-3xl bg-white px-6 pb-6 pt-16 shadow-2xl">
                    <div className="flex justify-center items-center mb-2">
                      <h2 className="text-center text-xl font-bold text-[#1e3a5f]">{`${child.first_name} ${child.last_name}`}</h2>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/edit-child-profile/${child.id}`);
                        }}
                        className="ml-2 p-2 rounded-full hover:bg-gray-100 transition-colors"
                        aria-label={`Edit ${child.first_name}'s profile`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L14.732 3.732z" />
                        </svg>
                      </button>
                    </div>
                    {child.pregnancystage ? (
                      <div className="mb-6 text-center">
                        <span className="inline-block bg-pink-100 text-pink-800 text-xs font-semibold mr-2 px-2.5 py-0.5 rounded-full">
                          Pregnancy Stage
                        </span>
                        <p className="text-sm text-gray-500 mt-2">Due {calculateTimeToBirth(child.dob)}</p>
                      </div>
                    ) : (
                      <p className="mb-6 text-center text-sm text-gray-500">{child.age_in_months ? `${Math.floor(child.age_in_months / 12)} years ${child.age_in_months % 12} months` : 'Age not available'}</p>
                    )}

                    {!child.pregnancystage && (
                    <div className="mb-4 grid grid-cols-2 gap-3">
                      <div className="flex flex-col items-center rounded-2xl bg-gradient-to-br from-blue-50 to-blue-100 p-4 shadow-sm">
                        <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow">
                          <svg className="h-5 w-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                          </svg>
                        </div>
                        <div className="text-xs font-medium text-gray-600">Height</div>
                        <div className="mt-1 text-lg font-bold text-[#1e3a5f]">{child.height_cm ? `${child.height_cm} cm` : 'N/A'}</div>
                      </div>

                      <div className="flex flex-col items-center rounded-2xl bg-gradient-to-br from-green-50 to-green-100 p-4 shadow-sm">
                        <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow">
                          <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                          </svg>
                        </div>
                        <div className="text-xs font-medium text-gray-600">Weight</div>
                        <div className="mt-1 text-lg font-bold text-[#1e3a5f]">{child.weight_kg ? `${child.weight_kg} kg` : 'N/A'}</div>
                      </div>
                    </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-full px-8 mt-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(child.pregnancystage ? `/pregnancy/${child.id}` : `/child/${child.id}`);
                  }}
                  className="w-full text-center rounded-xl bg-[#1e3a5f] py-3.5 text-sm font-semibold text-white shadow-md transition-all duration-300 opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0"
                >
                  Show Details
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-center pb-6 pt-6 ">
          <div ref={imageScrollRef} className="flex snap-x snap-mandatory gap-6 overflow-x-auto scrollbar-hide pt-4">
            {children.map((child, index) => (
              <button
                key={child.id}
                onClick={() => handleAvatarClick(index)}
                className={`flex flex-shrink-0 snap-center flex-col items-center transition-all duration-300 ${activeIndex === index ? "opacity-100" : "opacity-80"
                  }`}
              >
                <div
                  className={`mb-2 overflow-hidden rounded-full transition-all duration-300 ${activeIndex === index ? "h-24 w-24 scale-110 bg-[#FDB913] p-1.5 shadow-lg" : "h-20 w-20 scale-90 bg-gray-200"
                    }`}
                >
                  <img
                    src={child.profile_image}
                    alt={child.name}
                    className="h-full w-full rounded-full object-cover"
                    onLoad={() => console.log(`Avatar loaded for ${child.first_name}: ${child.profile_image}`)}
                    onError={(e) => console.error(`Error loading avatar for ${child.first_name}:`, e.target.src)}
                  />
                </div>
                <div className={`text-xs font-medium transition-all duration-300 ${activeIndex === index ? "text-gray-900" : "text-gray-500"}`}>{child.name}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex justify-center pb-6">
          <button  onClick={() => navigate('/create-child-profile')}
          className="relative flex h-12 w-12 items-center justify-center rounded-full bg-[#FDE9EB] shadow-md transition-transform active:scale-95">
            <svg className="h-6 w-6 text-gray-800" fill="currentColor" viewBox="0 0 24 24">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" />
            </svg>
            <svg className="absolute bottom-0 right-0 h-4 w-4 text-gray-800" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
            </svg>
          </button>
        </div>

      </div>

      <div className="relative flex justify-center bg-white px-5 pb-6 pt-3 shadow-[0_-2px_10px_rgba(0,0,0,0.05)]" style={{ zIndex: 10 }}>
        <button
          onClick={() => navigate('/create-child-profile')}
          className="w-full max-w-sm rounded-xl bg-[#1e3a5f] py-3.5 text-sm font-semibold text-white shadow-md transition-transform active:scale-98"
        >
          Register New Child
        </button>
      </div>

      <style jsx="true">{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
